import { ArrayField, BooleanField, ChipField, Datagrid, DateField, FunctionField, ImageField, List, NumberField, ReferenceArrayField, ReferenceField, RichTextField, SearchInput, SelectField, Show, SimpleShowLayout, SingleFieldList, Tab, TabbedShowLayout, TextField, useDataProvider, useGetOne, useListContext, usePermissions, useRecordContext } from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import Permission from '../../components/Permission';
import { PERMISSIONS, daysOfTheWeek, frequencyChoices } from '../../constants';

import { Box, Chip, Grid, Paper, Stack, Typography, Divider, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Skeleton, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { CheckChainUsesModule, GiftDisableButton, ModuleKey } from '../../components';
import { PartiallyHiddenCpfField } from '../../components/Fields';
import { getChainId, getUserId } from '../../lib';
import { DispatchMessageType, GiftExpirationType, GiftProductType, IntegrationType } from '../../models';
import { RoletaSegmentKind, RoletaSpinStatus } from '../../models/roleta';
import { DispatchMessageStatus } from '../../models/dispatchMessageStatus';
import { Link } from 'react-router-dom';
import { __t } from '../../i18n/t';
import CloneGiftButton from './CloneGiftButton';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { formatNumber } from '../../utils';
import { ProductBox, RuleList } from './components/show';
import 'moment/locale/pt-br';

const getCouponStatus = (coupon: any) => {
    if (coupon.refunded) return { label: 'Estornado', color: 'error' as const };
    if (coupon.redeemed) return { label: 'Utilizado', color: 'success' as const };
    if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) return { label: 'Expirado', color: 'warning' as const };
    return { label: 'Disponível', color: 'info' as const };
};

const getDiscountCouponStatus = (dc: any) => {
    if (dc.status === 'redeemed') return { label: 'Utilizado', color: 'success' as const };
    if (dc.status === 'expired') return { label: 'Expirado', color: 'warning' as const };
    return { label: 'Disponível', color: 'info' as const };
};

const getPredictionTicketStatus = (ticket: any) => {
    if (ticket.isWinner === true) return { label: 'Acertou', color: 'success' as const };
    if (ticket.isWinner === false) return { label: 'Não acertou', color: 'default' as const };
    if (ticket.submitted) return { label: 'Palpite enviado', color: 'info' as const };
    return { label: 'Aguardando palpite', color: 'warning' as const };
};

const getSpinTicketStatus = (spin: any) => {
    if (spin.status === RoletaSpinStatus.Expired) return { label: 'Venceu sem girar', color: 'default' as const };
    if (spin.status !== RoletaSpinStatus.Spun) return { label: 'Aguardando giro', color: 'warning' as const };
    if (spin.segmentKind === RoletaSegmentKind.Prize) return { label: 'Girou e ganhou', color: 'success' as const };
    if (spin.segmentKind === RoletaSegmentKind.ExtraSpin) return { label: 'Ganhou outro giro', color: 'info' as const };
    return { label: 'Passou a vez', color: 'default' as const };
};

const describeSpinTicket = (spin: any): string => {
    if (spin.status === RoletaSpinStatus.Expired) return 'O giro venceu antes de o cliente usar';
    if (spin.status !== RoletaSpinStatus.Spun) return 'Aguardando o cliente girar';
    if (spin.segmentKind === RoletaSegmentKind.Prize) return `Prêmio: ${spin.prizeName || spin.segmentLabel || '—'}`;
    if (spin.segmentKind === RoletaSegmentKind.ExtraSpin) return 'A roda deu outro giro ao cliente';
    return 'Não foi dessa vez';
};

// Componente expandable que mostra as recompensas
const AchievementExpandableRow = () => {
    const record = useRecordContext();

    if (!record) return null;

    const hasCoupons = record.coupons && record.coupons.length > 0;
    const hasDiscountCoupons = record.discountCoupons && record.discountCoupons.length > 0;
    const hasPredictionTickets = record.predictionTickets && record.predictionTickets.length > 0;
    // Giro na Roleta não gera cupom: a recompensa é a linha em roleta_spin.
    // Sem contar aqui, a conquista cai no "Nenhuma recompensa disponível".
    const hasSpinTickets = record.spinTickets && record.spinTickets.length > 0;
    // Número da Sorte também não gera cupom: a recompensa é a linha em
    // drawing_lucky_number (o backend enriquece a listagem com ela). Sem contar
    // aqui, toda conquista de sorteio — inclusive a comprada com pontos — cai no
    // "Nenhuma recompensa disponível".
    const hasLuckyNumbers = record.luckyNumbers && record.luckyNumbers.length > 0;

    if (!hasCoupons && !hasDiscountCoupons && !hasPredictionTickets && !hasSpinTickets && !hasLuckyNumbers) {
        return (
            <Box sx={{ p: 3, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Nenhuma recompensa disponível
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
            <Paper elevation={2} sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <LocalOfferIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Recompensas</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    {/* Cupons Regulares */}
                    {hasCoupons && record.coupons.map((coupon: any, index: number) => {
                        const status = getCouponStatus(coupon);
                        const couponNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={coupon.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#e3f2fd',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CardGiftcardIcon sx={{ fontSize: 28, color: '#1976d2' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Cupom"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={`#${couponNumber}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        color: '#666',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={status.label}
                                                    size="small"
                                                    color={status.color}
                                                />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {coupon.title || 'Cupom'}
                                            </Typography>
                                            {coupon.description && (
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    {coupon.description}
                                                </Typography>
                                            )}
                                            {coupon.barcode && (
                                                <Permission permission={PERMISSIONS.COUPONS_CODE}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Código: {coupon.barcode}
                                                    </Typography>
                                                </Permission>
                                            )}
                                            {coupon.expiresAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Validade: {moment(coupon.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Descontos de Combustível */}
                    {hasDiscountCoupons && record.discountCoupons.map((dc: any, index: number) => {
                        const status = getDiscountCouponStatus(dc);
                        const discountNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={dc.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#fff3e0',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <LocalGasStationIcon sx={{ fontSize: 28, color: '#f57c00' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Desconto Combustível"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#fff3e0',
                                                        color: '#f57c00',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={`#${discountNumber}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        color: '#666',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={status.label}
                                                    size="small"
                                                    color={status.color}
                                                />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                Desconto Combustível
                                            </Typography>
                                            {dc.expiresAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Validade: {moment(dc.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                            {dc.fuelDiscounts && dc.fuelDiscounts.length > 0 && (
                                                <Box mt={1}>
                                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                        Combustíveis:
                                                    </Typography>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {dc.fuelDiscounts.map((fd: any, idx: number) => (
                                                            <Chip
                                                                key={idx}
                                                                label={fd.fuelName || `Combustível ${idx + 1}`}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Direito ao Palpite (Bolão) */}
                    {hasPredictionTickets && record.predictionTickets.map((ticket: any, index: number) => {
                        const status = getPredictionTicketStatus(ticket);
                        const ticketNumber = String(index + 1).padStart(2, '0');
                        const matchLabel = ticket.bolaoName
                            || [ticket.homeTeamName, ticket.awayTeamName].filter(Boolean).join(' x ')
                            || 'Bolão';
                        const hasGuess = ticket.scoreHome !== null && ticket.scoreHome !== undefined
                            && ticket.scoreAway !== null && ticket.scoreAway !== undefined;

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={ticket.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#e8f5e9',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <SportsSoccerIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Direito ao Palpite"
                                                    size="small"
                                                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                                />
                                                <Chip
                                                    label={`#${ticketNumber}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#666', fontWeight: 600 }}
                                                />
                                                <Chip label={status.label} size="small" color={status.color} />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {matchLabel}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {hasGuess
                                                    ? `Palpite: ${ticket.scoreHome} x ${ticket.scoreAway}`
                                                    : 'Aguardando palpite do cliente'}
                                            </Typography>
                                            {ticket.scheduledAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Jogo: {moment(ticket.scheduledAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Giro na Roleta */}
                    {hasSpinTickets && record.spinTickets.map((spin: any, index: number) => {
                        const status = getSpinTicketStatus(spin);
                        const spinNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={spin.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#ede9fe',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CasinoIcon sx={{ fontSize: 28, color: '#7869DF' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Giro na Roleta"
                                                    size="small"
                                                    sx={{ bgcolor: '#ede9fe', color: '#7869DF', fontWeight: 600 }}
                                                />
                                                <Chip
                                                    label={`#${spinNumber}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#666', fontWeight: 600 }}
                                                />
                                                <Chip label={status.label} size="small" color={status.color} />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {spin.roletaName || 'Roleta da Sorte'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {describeSpinTicket(spin)}
                                            </Typography>
                                            {spin.expiresAt && spin.status !== RoletaSpinStatus.Spun && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Vence em: {moment(spin.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                            {spin.spunAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Girou em: {moment(spin.spunAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Número da Sorte */}
                    {hasLuckyNumbers && record.luckyNumbers.map((luckyNumber: any, index: number) => {
                        const numberIndex = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={luckyNumber.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#e8f5e9',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <ConfirmationNumberIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Número da Sorte"
                                                    size="small"
                                                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                                />
                                                <Chip
                                                    label={`#${numberIndex}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#666', fontWeight: 600 }}
                                                />
                                            </Stack>
                                            <Typography variant="h6" fontWeight={700} mb={0.5}>
                                                {luckyNumber.number}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {luckyNumber.drawingName || 'Sorteio'}
                                            </Typography>
                                            {luckyNumber.drawingDate && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Sorteio: {moment(luckyNumber.drawingDate).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                            {luckyNumber.assignedAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Concedido em: {moment(luckyNumber.assignedAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Box>
    );
};

const AchievementsFilter = [
    <DateTimeInputUTC source="from" label="Data Inicial" alwaysOn />,
    <DateTimeInputUTC source="to" label="Data Final" alwaysOn />,
];

const Empty = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>🏆</Typography>
        <Typography variant="body1" color="text.secondary">
            Nenhuma conquista registrada para este brinde
        </Typography>
    </Box>
);

const AchievementsList = () => {
    const record = useRecordContext();
    const theme = useTheme();

    if (!record) return null;

    // Filtro padrão: último mês às 00:00 até hoje às 23:59
    const to = moment().endOf('day').format('YYYY-MM-DDTHH:mm');
    const from = moment().subtract(1, 'month').startOf('day').format('YYYY-MM-DDTHH:mm');

    return (
        <List
            resource={`gifts/${record.id}/achievements`}
            title=" "
            actions={false}
            filters={AchievementsFilter}
            filterDefaultValues={{ from, to }}
            sort={{ field: 'createdAt', order: 'DESC' }}
            perPage={25}
            empty={<Empty />}
        >
            <Datagrid
                bulkActionButtons={false}
                expand={<AchievementExpandableRow />}
            >
                <FunctionField
                    label="Cliente (CPF)"
                    render={(record: any) => (
                        record.customerCpf ? (
                            <a
                                href={`#/customers/${record.customerId}/show`}
                                style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}
                            >
                                {record.customerCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                            </a>
                        ) : (
                            <Typography variant="body2" color="text.secondary">CPF não disponível</Typography>
                        )
                    )}
                />
                <DateField source="createdAt" label="Data e Hora da Conquista" showTime locales="pt-BR" />
            </Datagrid>
        </List>
    );
};

const CustomerList = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    // Brinde de campanha de recuperação não tem etiqueta nem grupo, e mesmo assim NÃO é
    // da rede inteira: quem o enxerga é só quem está inscrito e no nível dele. Quem aplica
    // isso é a gifts-api (RECOVERY_SCOPE_CONDITION, lendo customer_recovery_enrollment),
    // fora do alcance desta tela. Sem este ramo, o brinde cairia no "todos os clientes"
    // abaixo e a tela afirmaria o contrário do que acontece.
    if (record.recoveryLevelId) {
        const campaign = record.recoveryCampaign;
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Público definido pela campanha
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {__t('Só aparece para quem está inscrito na campanha e está no nível deste brinde; quem já conquistou sai da lista. A segmentação não é por etiqueta nem por grupo, então não há lista para mostrar aqui.')}
                    {campaign && (
                        <>
                            {' '}
                            <Link to={`/customer-recovery-rules/${campaign.id}/show`}>{campaign.name}</Link>
                            {__t(', nível %{order}.', { order: campaign.levelOrder })}
                        </>
                    )}
                </Typography>
            </Box>
        );
    }

    // Verifica se o brinde é para todos os clientes
    // (sem tags de clientes e sem grupo de clientes)
    const isForAllCustomers = (!record.tagIds || record.tagIds.length === 0) && !record.customerGroupId;

    if (isForAllCustomers) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>👥</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Brinde disponível para todos os clientes
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Este brinde não possui restrições de segmentação por tags ou grupos de clientes,
                    portanto está disponível para todos os clientes da rede.
                </Typography>
            </Box>
        );
    }

    const listFilters = [
        <SearchInput source="search" alwaysOn />
    ];

    const CustomerDatagrid = () => (
        <Datagrid
            bulkActionButtons={false}
            empty={
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum cliente encontrado com a segmentação definida para este brinde
                    </Typography>
                </Box>
            }
        >
            <TextField source="name" label="Nome" />
            <PartiallyHiddenCpfField source="cpf" label="CPF" />
        </Datagrid>
    );

    return (
        <List
            resource="customers"
            title=" - Clientes"
            filterDefaultValues={{ ...(record.tagIds?.length ? { tagIds: record.tagIds } : {}), chainId: record.chainId }}
            filters={listFilters}
            exporter={false}
            empty={
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum cliente encontrado com a segmentação definida para este brinde
                    </Typography>
                </Box>
            }
        >
            <CustomerDatagrid />
        </List>
    );
}

const NotificationList = (props) => {
    const record = useRecordContext();

    const Panel = () => (
        <ArrayField source="dispatchMessages" label="">
            <Datagrid bulkActionButtons={false}>
                <FunctionField render={record => record && DispatchMessageType.translateType(record.type)} label="Tipo" />
                <NumberField source="cardinality" label="Total" textAlign="center" />
                <FunctionField render={record => record && DispatchMessageStatus.translateType(record.status)} label="Status" textAlign="center" />
            </Datagrid>
        </ArrayField>
    );

    const NotificationCard = ({ notification, type }: { notification: any; type: 'creation' | 'achievement' }) => {
        const title = type === 'creation' ? 'Notificação de Criação' : 'Notificação de Conquista';
        const description = type === 'creation'
            ? 'Enviada quando o brinde é criado/publicado'
            : 'Enviada quando o usuário conquista o brinde';
        const icon = type === 'creation' ? '🎁' : '🏆';
        const color = type === 'creation' ? '#1976d2' : '#2e7d32';

        return (
            <Paper elevation={2} sx={{ p: 3, mb: 2, border: `2px solid ${color}20` }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <span style={{ fontSize: '24px' }}>{icon}</span>
                    <Typography variant="h6" fontWeight={600} color={color}>
                        {title}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {description}
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Título</Typography>
                        <Typography variant="body1" fontWeight={600}>{notification.title}</Typography>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Descrição</Typography>
                        <Box
                            dangerouslySetInnerHTML={{ __html: notification.body }}
                            sx={{
                                mt: 1,
                                p: 2,
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                '& img': { maxWidth: '100%' }
                            }}
                        />
                    </Grid>
                    {notification.pushNotification && (
                        <Grid size={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">Push Notification:</Typography>
                                <Chip label="Ativo" size="small" color="success" />
                            </Stack>
                            {notification.pushMessage && (
                                <Typography variant="body2" sx={{ mt: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                    {notification.pushMessage}
                                </Typography>
                            )}
                        </Grid>
                    )}
                    {notification.sms && notification.smsMessage && (
                        <Grid size={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">SMS:</Typography>
                                <Chip label="Ativo" size="small" color="success" />
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 1, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                {notification.smsMessage}
                            </Typography>
                        </Grid>
                    )}
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Envio</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {type === 'achievement'
                                ? (() => {
                                    const minutes = parseInt(notification.delayMinutes || '0');
                                    if (minutes === 0) {
                                      return 'Imediatamente após a conquista';
                                    } else if (minutes >= 60) {
                                      const hours = minutes / 60;
                                      return `${hours} ${hours === 1 ? 'hora' : 'horas'} após a conquista`;
                                    }
                                    return `${minutes} minutos após a conquista`;
                                  })()
                                : (() => {
                                    if (!notification.sendAt) return 'Imediatamente após a criação';
                                    const formatted = moment(notification.sendAt).format('DD/MM/YYYY [às] HH:mm');
                                    const isImmediate = notification.createdAt &&
                                        moment(notification.sendAt).diff(moment(notification.createdAt), 'minutes') <= 11;
                                    return isImmediate
                                        ? `Envio imediato (${formatted})`
                                        : `Agendado para ${formatted}`;
                                  })()
                            }
                        </Typography>
                    </Grid>

                    {/* Status do disparo (agregado por status no backend) */}
                    {notification.dispatchSummary && notification.dispatchSummary.length > 0 ? (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Status do disparo
                            </Typography>
                            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                {notification.dispatchSummary.map((dispatch: any, idx: number) => (
                                    <Box key={idx} sx={{ p: 1.5, borderBottom: idx < notification.dispatchSummary.length - 1 ? '1px solid #e0e0e0' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label={DispatchMessageType.translateType(dispatch.type)} size="small" />
                                            <Typography variant="body2">
                                                {type === 'achievement'
                                                    ? `${dispatch.count} cliente${dispatch.count > 1 ? 's' : ''}`
                                                    : `${dispatch.totalCardinality} destinatário${dispatch.totalCardinality > 1 ? 's' : ''}`}
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            label={DispatchMessageStatus.translateType(dispatch.status)}
                                            size="small"
                                            color={
                                                dispatch.status === DispatchMessageStatus.dispatched ? 'success'
                                                    : dispatch.status === DispatchMessageStatus.dispatchedWithError ? 'warning'
                                                        : dispatch.status === DispatchMessageStatus.failed ? 'error'
                                                            : dispatch.status === DispatchMessageStatus.scheduled || dispatch.status === DispatchMessageStatus.sending ? 'info'
                                                                : 'default'
                                            }
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    ) : (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Status do disparo
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Nenhum disparo gerado ainda.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        );
    };

    const creationNotification = record?.giftMarketingCampaigns?.find((n: any) => n.type === 'creation');
    const achievementNotification = record?.giftMarketingCampaigns?.find((n: any) => n.type === 'achievement');

    if (!creationNotification && !achievementNotification) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    Nenhuma notificação configurada para este brinde
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                {creationNotification && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <NotificationCard notification={creationNotification} type="creation" />
                    </Grid>
                )}
                {achievementNotification && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <NotificationCard notification={achievementNotification} type="achievement" />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

const LimitsBox = () => {
    const record = useRecordContext();

    if (!record) return null;

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} mb={2}>Limites e Validade</Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Limite de conquista</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {record.limitQuantity
                                ? `${record.limitQuantity} brinde${record.limitQuantity > 1 ? 's' : ''}`
                                : 'Ilimitado'
                            }
                        </Typography>
                    </Grid>
                    {record.limitIntervalInHours && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Período do limite</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                A cada {record.limitIntervalInHours} hora{record.limitIntervalInHours > 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                    )}
                    {record.expirationType && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Tipo de validade</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {GiftExpirationType.giftExpirationTypeChoices.find(c => c.id === record.expirationType)?.name || record.expirationType}
                            </Typography>
                        </Grid>
                    )}
                    {record.expirationType === GiftExpirationType.fixed && record.expirationTime && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Válido até</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {moment(record.expirationTime).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                        </Grid>
                    )}
                    {record.expirationType === GiftExpirationType.time && record.expirationInHours && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Válido por</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.expirationInHours} hora{record.expirationInHours > 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
}

const PlaceConfigBox = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [placesMap, setPlacesMap] = useState<Map<string, string>>(new Map());
    const [placesLoading, setPlacesLoading] = useState(false);
    const [discountPlacesMap, setDiscountPlacesMap] = useState<Map<string, any[]>>(new Map());

    // Signature of the place/discount ids this effect actually reads. The Show
    // controller first serves the gift from the list (TanStack Query) cache and
    // then refetches the full record via getOne — both share the same record.id,
    // so keying the effect on record?.id alone would skip the rebuild when
    // giftProducts/placeIds finally arrive, leaving the table stuck rendering raw
    // UUIDs until a full page reload. Keying on the ids fixes that without
    // refetching on every unrelated background refresh.
    const placesSignature = useMemo(() => {
        if (!record?.giftProducts) return '';
        const placeIds: string[] = [];
        const discountIds: string[] = [];
        record.giftProducts.forEach((giftProduct: any) => {
            giftProduct.giftProductPlaces?.forEach((placeConfig: any) => {
                if (placeConfig.placeId) placeIds.push(placeConfig.placeId);
            });
            if (giftProduct.productType === 'fuelDiscount' && giftProduct.discountId) {
                discountIds.push(giftProduct.discountId);
            }
        });
        return JSON.stringify({ places: placeIds.sort(), discounts: discountIds.sort() });
    }, [record]);

    useEffect(() => {
        if (!record || !record.giftProducts) return;

        let active = true;

        const placeIds = new Set<string>();
        record.giftProducts.forEach((giftProduct: any) => {
            giftProduct.giftProductPlaces?.forEach((placeConfig: any) => {
                if (placeConfig.placeId) placeIds.add(placeConfig.placeId);
            });
        });

        // Fetch discounts for fuelDiscount products
        const discountIds = record.giftProducts
            .filter((gp: any) => gp.productType === 'fuelDiscount' && gp.discountId)
            .map((gp: any) => gp.discountId);

        setPlacesLoading(true);

        // Fetch discounts first (if any fuelDiscount products)
        const discountsPromise = discountIds.length > 0
            ? dataProvider.getMany('discounts', { ids: discountIds })
            : Promise.resolve({ data: [] });

        discountsPromise
            .then(({ data: discounts }) => {
                // Map discountId -> placeIds (array of strings)
                const discPlacesMap = new Map<string, string[]>();
                discounts.forEach((d: any) => {
                    discPlacesMap.set(d.id, d.placeIds || []);
                    // Add placeIds from discount to fetch names
                    (d.placeIds || []).forEach((placeId: string) => {
                        placeIds.add(placeId);
                    });
                });
                if (active) setDiscountPlacesMap(discPlacesMap);

                // Now fetch all place names
                if (placeIds.size > 0) {
                    return dataProvider.getMany('places', { ids: Array.from(placeIds) });
                }
                return { data: [] };
            })
            .then(({ data: places }) => {
                if (!active) return;
                const map = new Map<string, string>();
                places.forEach((place: any) => {
                    map.set(place.id, place.name);
                });
                setPlacesMap(map);
            })
            .catch((error) => {
                console.error('Error fetching places:', error);
            })
            .finally(() => {
                if (active) setPlacesLoading(false);
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placesSignature, dataProvider]);

    if (!record || !record.giftProducts || record.giftProducts.length === 0) return null;

    const getProductTypeLabel = (type: string, cashValue?: number | null): { label: string; color: string; bgColor: string } => {
        // Recompensa em dinheiro é produto virtual no banco; o rótulo segue o que foi cadastrado.
        if (cashValue != null) {
            return { label: 'Cashback', color: '#2e7d32', bgColor: '#e8f5e9' };
        }

        switch (type) {
            case GiftProductType.Virtual:
                return { label: 'Produto Virtual', color: '#1976d2', bgColor: '#e3f2fd' };
            case GiftProductType.Existing:
                return { label: 'Produto Existente', color: '#7b1fa2', bgColor: '#f3e5f5' };
            case GiftProductType.FuelDiscount:
                return { label: 'Desconto Abastecimento', color: '#f57c00', bgColor: '#fff3e0' };
            case GiftProductType.PredictionTicket:
                return { label: 'Direito ao Palpite (Bolão)', color: '#7869DF', bgColor: '#ede9fe' };
            case GiftProductType.DrawingNumber:
                return { label: 'Número da Sorte (Sorteio)', color: '#00897b', bgColor: '#e0f2f1' };
            case GiftProductType.SpinTicket:
                return { label: 'Giro na Roleta', color: '#7869DF', bgColor: '#ede9fe' };
            default:
                return { label: type, color: '#666', bgColor: '#f5f5f5' };
        }
    };

    return (
        <Grid size={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>Locais de Resgate por Produto</Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {record.giftProducts.map((giftProduct: any, productIndex: number) => {
                        const hasPlaceConfig = giftProduct.giftProductPlaces && giftProduct.giftProductPlaces.length > 0;
                        const isFuelDiscount = giftProduct.productType === 'fuelDiscount';
                        const discountPlaces = isFuelDiscount && giftProduct.discountId
                            ? discountPlacesMap.get(giftProduct.discountId) || []
                            : [];
                        const hasDiscountPlaces = discountPlaces.length > 0;

                        // For fuelDiscount: show if has discount places
                        // For others: show if has giftProductPlaces
                        if (isFuelDiscount && !hasDiscountPlaces) return null;
                        if (!isFuelDiscount && !hasPlaceConfig) return null;

                        const productNumber = String(productIndex + 1).padStart(2, '0');
                        const typeInfo = getProductTypeLabel(giftProduct.productType, giftProduct.cashValue);

                        return (
                            <Grid size={{ xs: 12, md: 6 }} key={giftProduct.id || productIndex}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Produto #{productNumber}
                                    </Typography>
                                    <Chip
                                        label={typeInfo.label}
                                        size="small"
                                        sx={{
                                            bgcolor: typeInfo.bgColor,
                                            color: typeInfo.color,
                                            fontWeight: 600,
                                        }}
                                    />
                                </Stack>

                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                <TableCell><strong>Local</strong></TableCell>
                                                {!isFuelDiscount && (
                                                    <TableCell align="right"><strong>Valor parceiro</strong></TableCell>
                                                )}
                                                {giftProduct.productType === 'virtual' && (
                                                    <TableCell align="right"><strong>Desconto</strong></TableCell>
                                                )}
                                                {!isFuelDiscount && (
                                                    <TableCell align="center"><strong>Status</strong></TableCell>
                                                )}
                                                {!isFuelDiscount && (
                                                    <TableCell align="center"><strong>Integração</strong></TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {placesLoading ? (
                                                // show two skeleton rows while loading
                                                [0,1].map((s) => (
                                                    <TableRow key={`skeleton-${s}`}>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                                            <Skeleton variant="text" width={160} animation="wave" />
                                                        </TableCell>
                                                        {!isFuelDiscount && (
                                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                                <Skeleton variant="text" width={80} animation="wave" />
                                                            </TableCell>
                                                        )}
                                                        {giftProduct.productType === 'virtual' && (
                                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                                <Skeleton variant="text" width={60} animation="wave" />
                                                            </TableCell>
                                                        )}
                                                        {!isFuelDiscount && (
                                                            <TableCell align="center">
                                                                <Skeleton variant="text" width={60} animation="wave" />
                                                            </TableCell>
                                                        )}
                                                        {!isFuelDiscount && (
                                                            <TableCell align="center">
                                                                <Skeleton variant="text" width={60} animation="wave" />
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))
                                            ) : isFuelDiscount && hasDiscountPlaces ? (
                                                // Render places from discount for fuelDiscount products (placeIds is array of strings)
                                                discountPlaces.map((placeId: string, idx: number) => (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                                            {placesMap.get(placeId) || placeId}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                giftProduct.giftProductPlaces.map((placeConfig: any, idx: number) => (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                                            {placesMap.get(placeConfig.placeId) || placeConfig.placeId}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                            {placeConfig.partnerValue
                                                                ? formatNumber(placeConfig.partnerValue, { style: 'currency', currency: 'BRL' })
                                                                : '-'
                                                            }
                                                        </TableCell>
                                                        {giftProduct.productType === 'virtual' && (
                                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                                {placeConfig.discountPercentage !== null && placeConfig.discountPercentage !== undefined
                                                                    ? `${Number(placeConfig.discountPercentage).toFixed(2)}%`
                                                                    : '-'
                                                                }
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="center">
                                                            {placeConfig.hasProductPlace ? (
                                                                <Chip
                                                                    icon={<CheckCircleIcon />}
                                                                    label="Auto"
                                                                    size="small"
                                                                    color="success"
                                                                />
                                                            ) : (
                                                                <Chip label="Manual" size="small" color="warning" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                                                                {placeConfig.rtiCode && (
                                                                    <Chip label={IntegrationType.translateType(IntegrationType.rti)} size="small" color="info" />
                                                                )}
                                                                {placeConfig.isEvoActive && (
                                                                    <Chip label={IntegrationType.translateType(IntegrationType.evo)} size="small" color="success" />
                                                                )}
                                                                {placeConfig.trevoServiceId && (
                                                                    <Chip label={IntegrationType.translateType(IntegrationType.trevo)} size="small" color="primary" />
                                                                )}
                                                                {placeConfig.isTeixeiraPinto && (
                                                                    <Chip label={IntegrationType.translateType(IntegrationType.teixeiraPinto)} size="small" color="secondary" />
                                                                )}
                                                                {!placeConfig.rtiCode && !placeConfig.isEvoActive && !placeConfig.trevoServiceId && !placeConfig.isTeixeiraPinto && '-'}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Grid>
    );
}

const ScheduledBox = () => {
    const record = useRecordContext();

    if (!record) return null;

    const creationNotification = record.giftMarketingCampaigns?.find((n: any) => n.type === 'creation');

    const renderNotificationSendTime = () => {
        if (!creationNotification) {
            return <Typography variant="body1" fontWeight={600}>Sem notificação de criação</Typography>;
        }
        if (!creationNotification.sendAt) {
            return <Typography variant="body1" fontWeight={600}>Imediatamente após a criação</Typography>;
        }
        const formatted = moment(creationNotification.sendAt).format('DD/MM/YYYY [às] HH:mm');
        const isImmediate = creationNotification.createdAt &&
            moment(creationNotification.sendAt).diff(moment(creationNotification.createdAt), 'minutes') <= 11;
        return (
            <Typography variant="body1" fontWeight={600}>
                {isImmediate ? `Envio imediato (${formatted})` : `Agendado para ${formatted}`}
            </Typography>
        );
    };

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Agendamento</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Data inicial</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {record.startAt ? moment(record.startAt).format('DD/MM/YYYY HH:mm') : '-'}
                        </Typography>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Data final</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {record.endAt ? moment(record.endAt).format('DD/MM/YYYY HH:mm') : '-'}
                        </Typography>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Frequência</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {record.frequency ? (frequencyChoices.find(f => f.id === record.frequency)?.name || record.frequency) : 'Sem recorrência'}
                        </Typography>
                    </Grid>
                    <Grid size={12}>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">Envio da notificação de criação</Typography>
                        {renderNotificationSendTime()}
                    </Grid>
                </Grid>

                {record.frequency === 'weekly' && (
                    <Box mt={2}>
                        <Typography variant="subtitle2" mb={1}>Horários Semanais</Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Dia</strong></TableCell>
                                        <TableCell align="center"><strong>Horário</strong></TableCell>
                                        <TableCell align="center"><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {daysOfTheWeek.map((day) => {
                                        const schedule = record.dayScheduledGifts?.find((s: any) => s.dayOfTheWeek === day.id);
                                        const isActive = schedule && schedule.startTime && schedule.endTime;

                                        return (
                                            <TableRow key={day.id}>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                    {day.name}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                                                    {isActive
                                                        ? `${moment(schedule.startTime).format('HH:mm')} - ${moment(schedule.endTime).format('HH:mm')}`
                                                        : '-'
                                                    }
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isActive ? (
                                                        <Chip label="Ativo" size="small" color="success" />
                                                    ) : (
                                                        <Chip label="Inativo" size="small" color="default" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {record.frequency === 'daily' && (
                    <Grid container spacing={2} mt={1}>
                        <Grid size={6}>
                            <Typography variant="caption" color="text.secondary">Hora de início</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.dayScheduledGifts && record.dayScheduledGifts[0]?.startTime
                                    ? moment(record.dayScheduledGifts[0].startTime).format('HH:mm')
                                    : '-'
                                }
                            </Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="caption" color="text.secondary">Hora de fim</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {record.dayScheduledGifts && record.dayScheduledGifts[0]?.endTime
                                    ? moment(record.dayScheduledGifts[0].endTime).format('HH:mm')
                                    : '-'
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </Grid>
    );
}

const BasicInfoBox = () => {
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const dataProvider = useDataProvider();
    const [tagsMap, setTagsMap] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (!record || !record.tagIds || record.tagIds.length === 0) return;

        dataProvider.getMany('chains/chainId/tag', {
            ids: record.tagIds,
        }).then(({ data }) => {
            const map = new Map<string, string>();
            data.forEach((tag: any) => {
                map.set(tag.id, tag.name);
            });
            setTagsMap(map);
        }).catch(error => {
            console.error('Error fetching tags:', error);
        });
    }, [record, dataProvider]);

    if (!record) return null;

    return (
        <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <CardGiftcardIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Informações Básicas</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    {record.identify && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Código</Typography>
                            <Typography variant="body1" fontWeight={600}>{record.identify}</Typography>
                        </Grid>
                    )}
                    {record.name && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Nome do Brinde</Typography>
                            <Typography variant="body1" fontWeight={600}>{record.name}</Typography>
                        </Grid>
                    )}
                    {record.description && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Descrição</Typography>
                            <Typography variant="body1">{record.description}</Typography>
                        </Grid>
                    )}
                    {record.createdByEmployeeId && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Criado por</Typography>
                            <ReferenceField source="createdByEmployeeId" reference="employees" link={permissions && permissions.includes(PERMISSIONS.UPDATE_USER)}>
                                <FunctionField render={record => record ? (
                                    <Typography variant="body1" fontWeight={600}>
                                        {record.name} {record.id === getUserId() ? '(você)' : ''}
                                    </Typography>
                                ) : null} />
                            </ReferenceField>
                        </Grid>
                    )}
                    {record.tagIds && record.tagIds.length > 0 && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                Segmentação de Clientes:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {record.tagIds.map((tagId: string, index: number) => (
                                    <Chip
                                        key={index}
                                        label={tagsMap.get(tagId) || `Tag ID: ${tagId}`}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                ))}
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
}

/**
 * Brinde de campanha de recuperação: a campanha é dona dele. Quem liga, desliga e move a
 * janela é o avanço de nível — mexer aqui seria desfeito no próximo tick. Clonar também não
 * faz sentido: a cópia nasceria sem campanha e sem público.
 *
 * O aviso substitui os botões em vez de só desabilitá-los: o motivo importa mais que o botão.
 */
const RecoveryCampaignBanner = () => {
    const record = useRecordContext();
    const campaign = record?.recoveryCampaign;

    if (!campaign) {
        return (
            <Stack direction="row" spacing={2} mb={3}>
                <GiftDisableButton isOutlined />
                <CloneGiftButton />
            </Stack>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'warning.main' }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <CardGiftcardIcon fontSize="small" color="warning" />
                <Typography variant="body2">
                    {__t('Brinde do nível %{order} de uma campanha de recuperação. Ele é ligado e desligado pela campanha — para parar de conceder, encerre a campanha:', { order: campaign.levelOrder })}
                    {' '}
                    <Link to={`/customer-recovery-rules/${campaign.id}/show`}>{campaign.name}</Link>
                </Typography>
            </Stack>
        </Paper>
    );
};

const GiftShow = (props) => {

    const [chainId] = useState(getChainId());
    if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

    const { data: chain } = useGetOne('chains', { id: chainId });
    const { permissions } = usePermissions();

    return (
        <CheckChainUsesModule module={ModuleKey.GIFT}>
            <Permission permission={PERMISSIONS.GIFT}>
                <Show title='Brinde' {...props} sx={{ marginBottom: 5 }}>
                    <TabbedShowLayout >
                        <Tab label="Resumo">
                            <Box sx={{ p: 2 }}>
                                <RecoveryCampaignBanner />

                                <Grid container spacing={2}>
                                    {/* Linha 1: Informações Básicas, Limites e Validade, Agendamento */}
                                    <BasicInfoBox />
                                    <LimitsBox />
                                    <ScheduledBox />

                                    {/* Linha 2: Recompensas (toda a largura) */}
                                    <Grid size={12}>
                                        <ProductBox />
                                    </Grid>

                                    {/* Linha 3: Locais de Resgate por Produto (toda a largura) */}
                                    <PlaceConfigBox />
                                </Grid>
                            </Box>
                        </Tab>
                        <Tab label="Regras">
                            <RuleList chain={chain} padding={2} />
                        </Tab>
                        <Tab label="Clientes Elegíveis">
                            <CustomerList />
                        </Tab>
                        <Tab label="Notificações">
                            <Box sx={{ paddingTop: 2, paddingBottom: 5 }}>
                                <NotificationList />
                            </Box>
                        </Tab>
                        <Tab label="Conquistas">
                            <AchievementsList />
                        </Tab>
                    </TabbedShowLayout>
                </Show>
            </Permission>
        </CheckChainUsesModule>
    );
};

export default GiftShow;
